import React, { useState, useEffect, useRef } from 'react';
import { requestPhoneOtp, updatePhoneWithOtp } from '../../services/api';
import { Edit2, X } from 'lucide-react';

// 電話 OTP 認證流程,由 UserProfile.js 抽出嚟做獨立組件,等 PromoPage.js
// 呢類其他頁面都可以直接攞嚟用,唔使複製多份一樣嘅 request/confirm OTP 邏輯。
const PhoneVerification = ({ phone, onVerified, autoStart = false }) => {
  const [phoneStep, setPhoneStep] = useState(autoStart ? 'entering_phone' : 'idle');
  const [newPhone, setNewPhone] = useState(phone || '');
  const [otpCode, setOtpCode] = useState('');
  const [otpPrefix, setOtpPrefix] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);

  useEffect(() => {
    if (countdown > 0) {
      countdownRef.current = setInterval(() => setCountdown((c) => c - 1), 1000);
    } else {
      clearInterval(countdownRef.current);
    }
    return () => clearInterval(countdownRef.current);
  }, [countdown]);

  const startEditPhone = () => {
    setNewPhone(phone || '');
    setOtpCode('');
    setPhoneStep('entering_phone');
  };

  const cancelEditPhone = () => {
    setPhoneStep('idle');
    setOtpCode('');
  };

  const handleRequestPhoneOtp = async () => {
    if (!newPhone.trim()) return;
    setPhoneLoading(true);
    try {
      const response = await requestPhoneOtp(newPhone.trim());
      setOtpPrefix(response.data.prefix);
      setPhoneStep('entering_otp');
      setCountdown(60);
    } catch (err) {
      alert(`❌ ${err.response?.data?.error || '發送驗證碼失敗'}`);
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleConfirmPhoneOtp = async () => {
    if (otpCode.length !== 6) return;
    setPhoneLoading(true);
    try {
      const response = await updatePhoneWithOtp({
        phone_number: newPhone.trim(),
        otp_code: otpCode,
        otp_prefix: otpPrefix,
      });
      setPhoneStep('idle');
      setOtpCode('');
      alert('✅ 電話已更新！');
      if (onVerified) onVerified(response.data.raw_data);
    } catch (err) {
      alert(`❌ ${err.response?.data?.error || '驗證失敗'}`);
    } finally {
      setPhoneLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">會員電話</label>
        {phoneStep === 'idle' && (
          <button
            onClick={startEditPhone}
            className="flex items-center gap-1 text-xs font-bold text-blue-600"
          >
            <Edit2 size={12} /> {phone ? '變更' : '新增'}
          </button>
        )}
      </div>

      {phoneStep === 'idle' && (
        <p className="text-lg font-bold text-gray-800 ml-1">
          {phone || '未設定'}
        </p>
      )}

      {phoneStep === 'entering_phone' && (
        <div className="space-y-2">
          <input
            type="tel"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            placeholder="例如：0912345678"
            className="w-full bg-gray-50 border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-500 outline-none font-bold"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={handleRequestPhoneOtp}
              disabled={phoneLoading || !newPhone.trim()}
              className="flex-1 bg-blue-600 text-white py-2 rounded-xl font-bold disabled:bg-gray-300"
            >
              {phoneLoading ? '發送中...' : '傳送驗證碼'}
            </button>
            <button onClick={cancelEditPhone} className="p-2 text-gray-400"><X size={20} /></button>
          </div>
        </div>
      )}

      {phoneStep === 'entering_otp' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-400">
            驗證碼已傳送至 {newPhone}(編號 {otpPrefix}）
          </p>
          <input
            type="number"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.slice(0, 6))}
            placeholder="6 位數字驗證碼"
            className="w-full bg-gray-50 border-2 border-green-200 rounded-xl px-4 py-2 focus:border-green-500 outline-none font-bold text-center tracking-[0.4em]"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={handleConfirmPhoneOtp}
              disabled={phoneLoading || otpCode.length !== 6}
              className="flex-1 bg-green-600 text-white py-2 rounded-xl font-bold disabled:bg-gray-300"
            >
              {phoneLoading ? '驗證中...' : '確認'}
            </button>
            <button onClick={cancelEditPhone} className="p-2 text-gray-400"><X size={20} /></button>
          </div>
          <div className="text-center">
            {countdown > 0 ? (
              <p className="text-gray-400 text-xs">{countdown} 秒後可重新傳送</p>
            ) : (
              <button onClick={handleRequestPhoneOtp} className="text-blue-600 text-xs font-bold underline">
                重新傳送驗證碼
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneVerification;
